import React, { useMemo } from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';
import { Category, CategoryKey } from '../../data/services';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import { useAreaAvailability } from '../../hooks/useAreaAvailability';

/**
 * Home's categories as two swiped rows, not a wrapping grid.
 *
 * `CategoryGrid` is still the right shape for Bookings and for the full
 * catalogue picker - a fixed handful of trades that should all be visible at
 * once with no gesture. This is the opposite request: the same tiles, held
 * to a size small enough that several sit across a row's width, split into
 * two stacked rows rather than one long one so the block reads as a block
 * instead of a single thin strip - with the rest of each row reached by
 * swiping rather than by a third and fourth row pushing the rest of Home
 * down the page.
 *
 * The two rows scroll independently - there is no shared rail underneath
 * them - which is the same relationship the reference grid had between its
 * own two rows: two lines of the same tile size, not one list wrapped in
 * half.
 *
 * The plinth-and-cutout look is copied rather than shared with `CategoryGrid`
 * - that component solves for a *column count* from a measured row, which is
 * the opposite problem: here the count is fixed at three and the tile is
 * solved for instead.
 */

/**
 * Three across, filling the row.
 *
 * ---------------------------------------------------------------------------
 * THE TILE IS SOLVED FOR, NOT CHOSEN
 * ---------------------------------------------------------------------------
 * This started as a flat `TILE = 76`, then as a scaled `BASE_TILE = 92`. Both
 * were guesses at a width, and a guess always leaves a remainder: three 92pt
 * tiles on a 390pt phone use 292 of the 350pt available, so the row ended
 * short of the margin the block's own heading sits on and the categories read
 * as small objects floating in a wide card.
 *
 * So the count is the constant and the tile is the unknown. Three tiles plus
 * two gaps are made to equal the measured row exactly, which means the block
 * lines up with everything else on the page at every width, with nothing left
 * over. There is no `resp.scale` here any more - the row's own width already
 * carries the device size, and scaling a value that was derived from it would
 * apply the same growth twice.
 */
const COLUMNS = 3;
const GAP = spacing.sm;

/*
 * On a genuinely wide row the extra width buys more tiles, not bigger ones -
 * the same decision `CategoryGrid` documents at length. Three-across is a
 * design choice for a phone; a 1200pt web page solving three tiles out of its
 * row produces 400pt posters. The gate and the cap mirror the grid's, tuned
 * for these photo cards: `WIDE_ROW` sits above every phone (and at 520 also
 * below the 528pt row a native tablet's 560 column yields, whose three
 * 170pt tiles are already under the cap - so native layouts are unchanged
 * everywhere and only the web's wider page ever splits further).
 */
const WIDE_ROW = 520;
const MAX_TILE = 220;

/*
 * Everything else is a ratio of the tile, so the assembly scales as one object.
 *
 * ---------------------------------------------------------------------------
 * THESE TILES ARE FRAMED PICTURES, NOT STANDING CUTOUTS
 * ---------------------------------------------------------------------------
 * They used to be cutouts of professionals: a low plinth with a person
 * standing in front of it, head and shoulders clearing the top edge. Every
 * ratio below was shaped around that one effect - a short card, a figure
 * taller than it, and reserved headroom for what the figure escaped into.
 *
 * The artwork is now a still life of the trade's own tools, and the brief is
 * explicit that services are shown rather than faces - see `data/services.ts`.
 * A square, opaque photograph cannot stand in front of anything: it has edges,
 * so it needs a frame. Left on the old geometry it was a photo floating above
 * a mismatched green plinth, with the picture's own square corners cutting
 * across the card's rounded ones.
 *
 * So the card became the frame and the art fills it exactly. The row's old
 * reserved headroom went with the overhang that justified it, and the card is
 * square because the source images are.
 */
/** Card against tile. Just inset, so the tiles read as separate cards. */
const CARD_RATIO = 0.96;
/**
 * Card height against tile width.
 *
 * One, because the artwork is square and the card is its frame. Anything else
 * either letterboxes the photograph or crops it, and both look like a mistake
 * on a picture that was composed square.
 */
const HEIGHT_RATIO = CARD_RATIO;
/** The picture fills its frame exactly - it IS the card's face. */
const ART_RATIO = CARD_RATIO;

type Props = {
  categories: Category[];
  selected?: CategoryKey;
  onSelect?: (key: CategoryKey) => void;
};

/** The tile's measurements, derived once per width and shared by every tile. */
type Metrics = {
  columns: number;
  tile: number;
  height: number;
  card: number;
  art: number;
  glyph: number;
};

function Tile({
  category,
  active,
  onSelect,
  m,
}: {
  category: Category;
  active: boolean;
  onSelect?: (key: CategoryKey) => void;
  m: Metrics;
}) {
  /* Sized here rather than in the stylesheet: these depend on the measured
     width, and a `StyleSheet` entry is created once at module load. */
  /* See the same read in `CategoryGrid`. */
  const unavailable = useAreaAvailability().categories.has(category.key);

  const panel = {
    left: (m.tile - m.card) / 2,
    width: m.card,
    height: m.height,
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={category.label}
      accessibilityHint={unavailable ? 'Coming soon to this area' : undefined}
      onPress={() => onSelect?.(category.key)}
      style={({ pressed }) => [
        styles.cell,
        { width: m.tile },
        pressed ? styles.pressed : null,
        unavailable ? styles.dim : null,
      ]}
    >
      {/*
        One card either way, with either the picture or the fallback glyph
        inside it.

        These used to be two different assemblies - a plinth with a cutout
        floating over it, or a panel with an icon centred in it - because the
        artwork escaped the card's bounds. It no longer does, so the card is
        the frame in both cases and only its contents differ.

        The picture is a CHILD of the card rather than a sibling over it, which
        is what lets `overflow: 'hidden'` round the photograph's own square
        corners to match. As siblings those corners stayed square and cut
        visibly across the card beneath them.
      */}
      <View style={{ width: m.tile, height: m.height }}>
        <View style={[styles.panel, panel, active ? styles.panelActive : null]}>
          {category.image ? (
            <Image
              source={category.image}
              style={{ width: m.art, height: m.art }}
              /* `cover`, not `contain`: these are photographs that fill their
                 frame. `contain` would letterbox the picture inside the card
                 and leave a band of panel colour on two edges. */
              resizeMode="cover"
              /* Decode to the size actually drawn, and skip Android's
                 300ms fade - see the note in `ArtPanel`. */
              resizeMethod="resize"
              fadeDuration={0}
            />
          ) : (
            <Icon
              name={category.icon}
              size={m.glyph}
              color={colors.primary}
              strokeWidth={1.6}
            />
          )}
        </View>
      </View>

      <Text
        variant="caption"
        align="center"
        numberOfLines={2}
        color={active ? colors.primary : colors.inkBody}
      >
        {category.label}
      </Text>
    </Pressable>
  );
}

function CategoryRail({ categories, selected, onSelect }: Props) {
  const resp = useResponsive();

  /*
   * One set of measurements for the whole rail, recomputed only when the
   * width actually changes - a rotation, a fold, or split-screen.
   *
   * `resp.contentWidth` is the page's own column, which is the window on a
   * phone and a capped, centred column on a tablet. Taking the width from
   * there rather than from the window is what keeps the row aligned with the
   * heading above it on every device.
   */
  const m = useMemo<Metrics>(() => {
    const row = resp.contentWidth - screenPadding * 2;
    const tileAt = (n: number) => Math.floor((row - GAP * (n - 1)) / n);

    /* Three across, until a wide row would make posters of them - then the
       count grows while the tiles are over the cap. Never past the number of
       categories there are: a column with nothing to put in it only shrinks
       the tiles that exist. See WIDE_ROW/MAX_TILE above. */
    let columns = COLUMNS;
    while (
      row >= WIDE_ROW &&
      tileAt(columns) > MAX_TILE &&
      columns < categories.length
    ) {
      columns += 1;
    }

    /* The unknown: the tiles and gaps fill the row exactly. */
    const tile = tileAt(columns);
    const height = Math.round(tile * HEIGHT_RATIO);
    const art = Math.round(tile * ART_RATIO);
    return {
      columns,
      tile,
      height,
      card: Math.round(tile * CARD_RATIO),
      art,
      /*
       * The fallback glyph, for a category with no artwork.
       *
       * Measured against the card's height, which is now the same as its
       * width - the card is square. 0.42 leaves a comfortable margin on all
       * four sides rather than the tight top-and-bottom the old low plinth
       * forced.
       */
      glyph: Math.round(height * 0.42),
    };
  }, [categories.length, resp]);
  /*
   * Fixed rows of three.
   *
   * This used to split down the middle, which was right when the row scrolled
   * and the halves were free to be any length. Now that three tiles fill the
   * width exactly, a half of four would overflow it - so the row size is the
   * column count, and six categories land as the 3/3 the reference shows.
   */
  const rows = useMemo(() => {
    const out: Category[][] = [];
    for (let i = 0; i < categories.length; i += m.columns) {
      out.push(categories.slice(i, i + m.columns));
    }
    return out;
  }, [categories, m.columns]);

  return (
    <View style={styles.rows}>
      {rows
        .filter(row => row.length > 0)
        .map((row, i) => (
          /*
             A plain row, not a scroller. Three tiles now measure the full
             width, so there is nothing left to scroll to - and a horizontal
             ScrollView that cannot scroll still swallows horizontal drags,
             which on Home is the gesture that moves the hero.
          */
          <View
            key={i}
            style={[
              styles.row,
              { paddingHorizontal: screenPadding, gap: GAP },
            ]}
          >
            {row.map(category => (
              <Tile
                key={category.key}
                category={category}
                active={category.key === selected}
                onSelect={onSelect}
                m={m}
              />
            ))}
            {/* Pads a short final row so its tiles keep the width of those
                above rather than stretching to fill. */}
            {Array.from({ length: m.columns - row.length }, (_, pad) => (
              <View key={`pad-${pad}`} style={{ width: m.tile }} />
            ))}
          </View>
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  dim: {
    opacity: 0.45,
  },
  rows: {
    gap: spacing.sm,
  },
  row: {
    flexDirection: 'row',
  },
  /* Width is set per-render from the measured tile. */
  cell: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  pressed: {
    opacity: 0.6,
  },
  /* Sized and positioned by the caller; only the shared floor is fixed here. */
  panel: {
    position: 'absolute',
    bottom: 0,
    borderRadius: radii.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    /* Clips the photograph to the card's rounded corners. Without it the
       picture's square corners sit proud of the frame on all four sides. */
    overflow: 'hidden',
    ...hairline,
  },
  panelActive: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
});

export default React.memo(CategoryRail);
